<script setup lang="ts">
import PdsButton from '@/components/pds/PdsButton.vue';
import PdsIcon from '@/components/pds/PdsIcon.vue';
import { computed, reactive, ref, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import CancelOrderModal from '../components/CancelOrderModal.vue';
import InfoCard from '../components/InfoCard.vue';
import OrderChangesModal from '../components/OrderChangesModal.vue';
import PageBar from '../components/PageBar.vue';
import PageTitle from '../components/PageTitle.vue';
import PickupSlotModal from '../components/PickupSlotModal.vue';
import { formatDateTime, formatMoney, formatPickup } from '../format';
import { buildChanges, totalRefundCents, type ItemEdit } from '../orderEdit';
import { usePdsOrdersStore } from '../stores/orders.store';
import type { PdsCancellation } from '../types';

const store = usePdsOrdersStore();
const route = useRoute();
const router = useRouter();

const order = computed(() => store.byId(String(route.params.id)));
const edits = reactive<Record<string, ItemEdit>>({});
const validating = ref(false);
const cancelling = ref(false);
const rescheduling = ref(false);
const busy = ref(false);

const backToOrder = computed(() => `/commande/${route.params.id}`);

// Seule une commande en préparation se modifie : sinon l'écran n'a rien à montrer.
watchEffect(() => {
  if (store.loadStatus !== 'ready') return;
  if (order.value?.stage !== 'preparing') router.replace('/');
});

const editOf = (itemId: string, quantity: number) =>
  (edits[itemId] ??= { quantity, removed: false });

const lower = (itemId: string, quantity: number) => {
  const edit = editOf(itemId, quantity);
  if (edit.quantity > 1) edit.quantity -= 1;
};
const toggleRemoved = (itemId: string, quantity: number) => {
  const edit = editOf(itemId, quantity);
  edit.removed = !edit.removed;
};

const shown = (itemId: string, quantity: number) =>
  edits[itemId]?.quantity ?? quantity;
const isRemoved = (itemId: string) => edits[itemId]?.removed ?? false;

const changes = computed(() =>
  order.value ? buildChanges(order.value, edits) : [],
);
const refundCents = computed(() => totalRefundCents(changes.value));
// Vider la commande revient à l'annuler : le serveur refuse l'un, propose l'autre.
const emptied = computed(
  () =>
    !!order.value &&
    changes.value.length > 0 &&
    changes.value.every((change) => change.to === 0) &&
    changes.value.length === order.value.items.length,
);

const saveChanges = async ({ reasons, message }: PdsCancellation) => {
  if (busy.value || !changes.value.length || emptied.value) return;
  busy.value = true;
  const saved = await store.adjustLines(
    order.value!.id,
    changes.value.map((change) => ({
      orderItemId: change.item.id,
      qty: change.to,
    })),
    message,
    reasons,
  );
  busy.value = false;
  if (saved) router.replace(backToOrder.value);
  else validating.value = false;
};

const reschedule = async (pickupTime: number) => {
  if (busy.value) return;
  busy.value = true;
  const saved = await store.reschedule(order.value!.id, pickupTime);
  busy.value = false;
  if (saved) rescheduling.value = false;
};

const cancelOrder = async (cancellation: PdsCancellation) => {
  if (busy.value) return;
  busy.value = true;
  const saved = await store.cancelOrder(order.value!.id, cancellation);
  busy.value = false;
  if (saved) router.replace('/');
  else cancelling.value = false;
};
</script>

<template>
  <template v-if="order">
    <PageBar tall :back-to="backToOrder" back-label="Commande">
      <PageTitle
        title="Modifier la commande"
        :subtitle="`#${order.orderNumber ?? order.number} · ${order.label} · retrait ${formatPickup(order.dueAt)}`"
      />
    </PageBar>

    <main
      class="no-scrollbar grid min-h-0 flex-1 grid-cols-1 items-start gap-5 overflow-y-auto px-4 pt-[18px] pb-4 sm:px-5 sm:pb-5 lg:grid-cols-[1fr_380px] lg:items-stretch"
    >
      <section class="flex flex-col gap-[9px] pt-[10px]">
        <h2
          class="text-encre-douce text-[12px] leading-[14px] font-semibold tracking-[0.9px] uppercase"
        >
          Produits de la commande
          <span class="text-encre ml-2">{{ order.items.length }}</span>
        </h2>
        <ul class="bg-carte rounded-[32px] px-4 pt-[13px] pb-[14px]">
          <li
            v-for="item in order.items"
            :key="item.id"
            class="border-filet-doux flex min-h-[65px] items-center gap-3 border-b py-2 last:border-b-0"
          >
            <button
              type="button"
              aria-label="Diminuer"
              class="bg-creux flex size-12 shrink-0 cursor-pointer items-center justify-center rounded-full disabled:cursor-not-allowed disabled:opacity-40"
              :disabled="
                isRemoved(item.id) || shown(item.id, item.quantity) <= 1
              "
              @click="lower(item.id, item.quantity)"
            >
              <PdsIcon name="minus" />
            </button>
            <span
              class="w-9 shrink-0 text-right text-[14px] leading-[24px] font-semibold"
              :class="isRemoved(item.id) && 'text-encre-douce line-through'"
            >
              {{ shown(item.id, item.quantity)
              }}<span class="text-[12px] font-normal">×</span>
            </span>
            <span class="flex min-w-0 flex-1 flex-col">
              <span
                class="text-[14px] leading-[24px] font-semibold"
                :class="isRemoved(item.id) && 'text-encre-douce line-through'"
              >
                {{ item.name }}
              </span>
              <span
                v-if="item.option"
                class="text-encre-douce text-[12px] leading-[1.45]"
              >
                {{ item.option }}
              </span>
            </span>
            <span
              v-if="item.unitPriceCents !== undefined"
              class="shrink-0 text-[14px] leading-[24px] font-semibold"
              :class="isRemoved(item.id) && 'text-encre-douce line-through'"
            >
              {{
                formatMoney(shown(item.id, item.quantity) * item.unitPriceCents)
              }}
            </span>
            <button
              type="button"
              class="border-second-trait text-encre h-12 shrink-0 cursor-pointer rounded-[12px] border px-5 text-[14px] leading-[20px] font-semibold"
              @click="toggleRemoved(item.id, item.quantity)"
            >
              {{ isRemoved(item.id) ? 'Rétablir' : 'Supprimer' }}
            </button>
          </li>
        </ul>
      </section>

      <div class="flex flex-col gap-3">
        <div
          class="bg-carte flex items-center gap-3 rounded-[24px] px-5 py-[14px]"
        >
          <p class="flex min-w-0 flex-1 flex-col gap-[2px]">
            <span class="text-encre-douce text-[12px] leading-[17px]">
              Retrait
            </span>
            <span class="text-[14px] leading-[20px] font-semibold">
              {{ formatDateTime(order.dueAt) }}
            </span>
          </p>
          <button
            type="button"
            aria-label="Changer le retrait"
            class="text-encre-douce -m-4 flex size-12 shrink-0 cursor-pointer items-center justify-center"
            @click="rescheduling = true"
          >
            <PdsIcon name="edit" :size="16" />
          </button>
        </div>

        <InfoCard label="À rembourser">
          <div class="flex flex-col gap-[14px] py-[7px]">
            <p class="text-[32px] leading-8 font-bold tracking-[-0.4px]">
              {{ formatMoney(refundCents) }}
            </p>
            <ul v-if="changes.length" class="flex flex-col gap-[6px]">
              <li
                v-for="change in changes"
                :key="change.item.id"
                class="flex items-baseline gap-[10px] text-[14px] leading-[20px]"
              >
                <span class="min-w-0 flex-1 truncate">
                  {{ change.item.name }}
                </span>
                <span class="text-encre-douce text-[12px]">
                  {{ change.to ? `${change.from} → ${change.to}` : 'supprimé' }}
                </span>
                <span class="w-[72px] text-right">
                  −{{ formatMoney(change.refundCents) }}
                </span>
              </li>
            </ul>
            <p class="text-encre-douce text-[12px] leading-[17px]">
              Montant estimé d'après le prix de chaque ligne : le serveur
              calcule et rembourse le montant exact. Le client en est informé
              par SMS.
            </p>
            <p
              v-if="emptied"
              role="alert"
              class="text-alerte-texte text-[12px] leading-[17px]"
            >
              Retirer tous les produits revient à annuler la commande.
            </p>
          </div>
        </InfoCard>

        <div class="mt-auto flex flex-col gap-2 pt-3">
          <hr class="border-filet-doux" />
          <button
            type="button"
            class="border-second-trait text-encre h-12 w-full cursor-pointer rounded-[12px] border text-[14px] leading-[1.4] font-semibold"
            @click="cancelling = true"
          >
            Annuler toute la commande
          </button>
          <p class="text-encre-douce text-[12px] leading-[17px]">
            Exige au moins un motif. Le remboursement sera intégral.
          </p>
        </div>
      </div>
    </main>

    <footer
      class="bg-carte border-filet flex min-h-[94px] shrink-0 flex-wrap items-center justify-between gap-3 border-t px-4 py-4 sm:px-5"
    >
      <PdsButton variant="second" fit :to="backToOrder">
        Abandonner les modifications
      </PdsButton>
      <PdsButton
        fit
        :disabled="!changes.length || emptied"
        @click="validating = true"
      >
        Rembourser {{ formatMoney(refundCents) }}
      </PdsButton>
    </footer>

    <OrderChangesModal
      v-if="validating"
      :order="order"
      :changes="changes"
      :refund-cents="refundCents"
      :busy="busy"
      @close="validating = false"
      @confirm="saveChanges"
    />
    <PickupSlotModal
      v-if="rescheduling"
      :order="order"
      :busy="busy"
      :load-choices="store.loadPickupChoices"
      @close="rescheduling = false"
      @confirm="reschedule"
    />
    <CancelOrderModal
      v-if="cancelling"
      :order="order"
      :busy="busy"
      @close="cancelling = false"
      @confirm="cancelOrder"
    />
  </template>
</template>
